import React, { useEffect, useState } from "react";
import LinearProgress from "@mui/material/LinearProgress";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import OAuth from "../../config/autodeskApi";
import { styled } from "@mui/material/styles";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import UploadModelService from "../../services/uploadModelService";
import { v4 as uuidv4 } from "uuid";
import LogHistoryService from "../../services/logHistory";
import { bucketName } from "../../config/master";
import { Dialog, DialogTitle, CircularProgress } from "@mui/material";
import { useNavigate } from "react-router-dom";

const LoadingModal = ({ open }) => (
  <Dialog open={open}>
    <DialogTitle style={{ textAlign: "center" }}>
      กำลังแปลงไฟล์...
      <CircularProgress style={{ display: "block", margin: "10px auto" }} />
    </DialogTitle>
  </Dialog>
);

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.mode === "dark" ? "#1A2027" : "#fff",
  ...theme.typography.body2,
  padding: theme.spacing(1),
  textAlign: "center",
  color: theme.palette.text.secondary,
}));

const UploadModel = ({ handleSaveUploadModel }) => {
  const navigate = useNavigate();

  const [selectedFiles, setSelectedFiles] = useState([]);
  const [nameModel, setNameModel] = useState("");
  const [access_Token, setAccess_Token] = useState("");
  const [expires_in, setExpires_in] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [status, setStatus] = useState("ไม่มีการแปลงไฟล์");
  const [checkBucket, setCheckBucket] = useState(true);
  const [bucket, setBucketName] = useState(bucketName);
  const [convertData, setConvertData] = useState("");
  const [nameProject, setNameProject] = useState("");
  const [uploadPercentage, setUploadPercentage] = useState(0);

  useEffect(() => {
    fetchToken();
  }, []);

  const fetchToken = async () => {
    try {
      const response = await OAuth.GetToken();
      console.log("Token response:", response);
      setAccess_Token(response.access_token);
      setExpires_in(response.expires_in);
      await onCheckBucket(response.access_token);
      return response.access_token;
    } catch (error) {
      console.error("Failed to fetch token:", error);
      alert("Unable to fetch token. Please try again later.");
      throw error;
    }
  };

  const onCheckBucket = async (token) => {
    try {
      const response = await UploadModelService.CheckBucket(token);
      console.log("Bucket check response:", response.data);

      if (response?.data?.items?.length > 0) {
        setCheckBucket(true);
      } else {
        console.log("No bucket found, creating one.");
        setCheckBucket(false);
      }
    } catch (error) {
      console.error("Failed to check bucket:", error.response?.data || error.message);
      setCheckBucket(false);
    }
  };

  const submitHandler = async (e) => {
    e.preventDefault();

    try {
      let token = access_Token;
      if (!token) {
        token = await fetchToken();
      }

      if (selectedFiles.length === 0) {
        alert("กรุณาเลือกไฟล์ก่อนอัพโหลด");
        return;
      }

      if (!nameProject.trim()) {
        alert("กรุณากรอกชื่อโครงการ");
        return;
      }

      if (!nameModel.trim()) {
        alert("กรุณากรอกชื่อโมเดล");
        return;
      }

      const file = selectedFiles[0];
      const fileExt = file.name.split(".").pop().toLowerCase();
      const model_ref = uuidv4();
      const objectKey = `${model_ref}.${fileExt}`;

      if (!checkBucket) {
        try {
          console.log("Creating bucket...");
          await UploadModelService.CreateBucket(token, bucket);
          setCheckBucket(true);
        } catch (error) {
          console.error("Failed to create bucket:", error.response?.data || error.message);
          alert("Failed to create bucket. Please try again later.");
          return;
        }
      }

      // 1) ขอ signed URL
      const signedUploadRes = await UploadModelService.GetSignedS3UploadUrl(
        token,
        bucket,
        objectKey
      );

      console.log("signed upload response:", signedUploadRes.data);

      const signedUrl =
        signedUploadRes.data.urls?.[0] ||
        signedUploadRes.data.url ||
        signedUploadRes.data.signedUrl;

      const uploadKey = signedUploadRes.data.uploadKey;

      if (!signedUrl) {
        throw new Error("ไม่พบ signedUrl สำหรับอัปโหลดไฟล์");
      }

      // 2) upload ไฟล์ไปยัง signed URL
      await UploadModelService.UploadFileToSignedUrl(
        signedUrl,
        file,
        (progressEvent) => {
          const total = progressEvent.total || file.size || 1;
          const percentCompleted = Math.round((progressEvent.loaded * 100) / total);
          setUploadPercentage(percentCompleted);

          if (percentCompleted === 100) {
            setStatus("กำลังแปลงไฟล์");
            setShowModal(true);
          }
        }
      );

      // 3) complete upload
      const completeRes = await UploadModelService.CompleteSignedS3Upload(
        token,
        bucket,
        objectKey,
        uploadKey
      );

      console.log("complete upload response:", completeRes.data);

      // 4) สร้าง URN
      const objectId =
        completeRes.data.objectId ||
        signedUploadRes.data.objectId ||
        `urn:adsk.objects:os.object:${bucket}/${objectKey}`;

      const encodedUrn = window.btoa(objectId);
      console.log("Get URN Success urn:", encodedUrn);

      // 5) translate
      const translateResponse = await UploadModelService.Translate(token, encodedUrn);
      console.log("Translate response:", translateResponse.data);
      setConvertData(translateResponse.data);

      const urn = translateResponse.data.urn || encodedUrn;

      let check = true;
      let attempts = 0;
      const maxAttempts = 300;

      while (check && attempts < maxAttempts) {
        attempts += 1;
        await new Promise((resolve) => setTimeout(resolve, 3000));

        const getUrnResponse = await UploadModelService.ConvertFile(token, urn);
        const manifest = getUrnResponse.data;

        console.log("manifest:", manifest);

        if (manifest.status === "success" || manifest.progress === "complete") {
          check = false;
          console.log("Conversion complete.");

          const uidData = JSON.parse(localStorage.getItem("uid"));
          const currentdate = new Date();

          const data = {
            owner: uidData.uid,
            project_name: nameProject,
            name_model: nameModel,
            model_ref: model_ref,
            token_model: "urn:" + encodedUrn,
            create_byname: uidData.first_name,
            create_bylastname: uidData.last_name,
            create_date: currentdate,
            type_model: fileExt,
            users: [uidData.uid],
            company_id: uidData.company_id,
          };

          if (typeof handleSaveUploadModel === "function") {
            await handleSaveUploadModel(data);

            const logData = {
              uid: uidData.uid,
              email: uidData.email,
              first_name: uidData.first_name,
              last_name: uidData.last_name,
              time: new Date(),
              message: "เพิ่มโมเดล",
              model_ref: model_ref,
              name_model: nameModel,
            };

            try {
              await LogHistoryService.Add(uidData.company_id, logData);
            } catch (error) {
              console.log("Log history error:", error);
            }
          }

          setShowModal(false);
          alert("แปลงไฟล์เสร็จสิ้นแล้ว");
          navigate("/viewer");
          setStatus("Conversion Complete");
        } else if (manifest.status === "failed") {
          throw new Error(
            manifest?.diagnostic?.message ||
              manifest?.messages?.[0]?.message ||
              "Model translation failed"
          );
        } else {
          setStatus("กำลังแปลงไฟล์");
          setShowModal(true);
        }
      }

      if (attempts >= maxAttempts) {
        throw new Error("Translation timeout");
      }
    } catch (error) {
      console.error("Failed to convert or upload:", {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data,
      });

      setStatus("Error in conversion");
      setShowModal(false);

      alert(
        error.response?.data?.reason ||
          error.response?.data?.developerMessage ||
          error.message ||
          "Failed to upload or convert the model. Please try again later."
      );
    }
  };

  return (
    <Grid container spacing={2}>
      {!checkBucket ? (
        <Grid item xs={12}>
          <TextField
            id="standard-basic"
            label="ชื่อ Bucket"
            fullWidth
            onChange={(e) => setBucketName(e.target.value)}
            value={bucket}
            sx={{
              "& label.Mui-focused": {
                color: "#FF8B00",
              },
              "& .MuiInput-underline:after": {
                borderBottomColor: "#FF8B00",
              },
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderRadius: "2rem",
                },
                "&:hover fieldset": {
                  borderColor: "#FF8B00",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#FF8B00",
                },
                borderRadius: "2rem",
                backgroundColor: "white",
              },
            }}
          />
        </Grid>
      ) : null}

      <Grid item xs={12}>
        <TextField
          id="standard-basic"
          label="ชื่อโครงการ"
          fullWidth
          onChange={(e) => setNameProject(e.target.value)}
          value={nameProject}
          sx={{
            "& label.Mui-focused": {
              color: "#FF8B00",
            },
            "& .MuiInput-underline:after": {
              borderBottomColor: "#FF8B00",
            },
            "& .MuiOutlinedInput-root": {
              "& fieldset": {
                borderRadius: "2rem",
              },
              "&:hover fieldset": {
                borderColor: "#FF8B00",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#FF8B00",
              },
              borderRadius: "2rem",
              backgroundColor: "white",
            },
          }}
        />
      </Grid>

      <Grid item xs={12}>
        <TextField
          id="standard-basic"
          label="ชื่อโมเดล"
          fullWidth
          onChange={(e) => setNameModel(e.target.value)}
          value={nameModel}
          sx={{
            "& label.Mui-focused": {
              color: "#FF8B00",
            },
            "& .MuiInput-underline:after": {
              borderBottomColor: "#FF8B00",
            },
            "& .MuiOutlinedInput-root": {
              "& fieldset": {
                borderRadius: "2rem",
              },
              "&:hover fieldset": {
                borderColor: "#FF8B00",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#FF8B00",
              },
              borderRadius: "2rem",
              backgroundColor: "white",
            },
          }}
        />
      </Grid>

      <Grid item xs={12}>
        <input
          id="contained-button-file"
          type="file"
          multiple
          onChange={(e) => setSelectedFiles(e.target.files)}
          style={{ display: "none" }}
        />
        <label htmlFor="contained-button-file">
          <Button
            variant="contained"
            component="span"
            sx={{
              backgroundColor: "#FF8B00",
              "&:hover": {
                backgroundColor: "#FF6F00",
              },
            }}
          >
            เลือกไฟล์
          </Button>
        </label>
      </Grid>

      <Grid item xs={12}>
        <LinearProgress variant="determinate" value={uploadPercentage} />
      </Grid>

      <Grid item xs={12}>
        <Button
          variant="contained"
          onClick={submitHandler}
          sx={{
            backgroundColor: "#FF8B00",
            "&:hover": {
              backgroundColor: "#FF6F00",
            },
          }}
        >
          อัพโหลด
        </Button>
      </Grid>

      <LoadingModal open={showModal} />
    </Grid>
  );
};

export default UploadModel;